<script setup lang="ts">
import { computed } from "vue";

export type ChipColor =
  | "gray"
  | "yellow"
  | "red"
  | "green"
  | "orange"
  | "blue"
  | "purple";

const props = defineProps<{
  dense?: boolean;
  small?: boolean;
  outlined?: boolean;
  color?: ChipColor;
  disabled?: boolean;
}>();

const dynamicClasses = computed(() => {
  const classes = ["tw:whitespace-nowrap"];
  if (props.small) {
    classes.push("tw:text-xs");
  } else {
    classes.push("tw:text-sm");
  }
  if (props.dense) {
    classes.push("tw:py-1", "tw:px-2");
  } else {
    classes.push("tw:py-2", "tw:px-3");
  }
  if (props.outlined) {
    classes.push("tw:outline", "tw:outline-4");
  }
  if (props.color === "gray") {
    classes.push(
      "tw:bg-zinc-200",
      "tw:dark:bg-zinc-600",
      "tw:text-zinc-600!",
      "tw:dark:text-zinc-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-zinc-500/50");
    }
  }
  if (props.color === "yellow") {
    classes.push(
      "tw:bg-yellow-200",
      "tw:dark:bg-yellow-700",
      "tw:text-yellow-600!",
      "tw:dark:text-yellow-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-yellow-500/50");
    }
  }
  if (props.color === "red") {
    classes.push(
      "tw:bg-red-200",
      "tw:dark:bg-red-800",
      "tw:text-red-600!",
      "tw:dark:text-red-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-red-500/40");
    }
  }
  if (props.color === "green") {
    classes.push(
      "tw:bg-success-200",
      "tw:dark:bg-success-700",
      "tw:text-success-600!",
      "tw:dark:text-success-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-success-500/50");
    }
  }
  if (props.color === "orange") {
    classes.push(
      "tw:bg-orange-200",
      "tw:dark:bg-orange-800",
      "tw:text-orange-700!",
      "tw:dark:text-orange-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-orange-500/50");
    }
  }

  if (props.color === "blue") {
    classes.push(
      "tw:bg-blue-200",
      "tw:dark:bg-blue-700",
      "tw:text-blue-600!",
      "tw:dark:text-blue-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-blue-500/50");
    }
  }

  if (props.color === "purple") {
    classes.push(
      "tw:bg-purple-200",
      "tw:dark:bg-purple-700",
      "tw:text-purple-600!",
      "tw:dark:text-purple-200!",
      "tw:font-semibold",
    );
    if (props.outlined) {
      classes.push("tw:outline-purple-500/50");
    }
  }

  if (props.disabled) {
    classes.push("tw:text-zinc-500!", "tw:cursor-not-allowed");
  }
  return classes;
});

const emit = defineEmits<{
  (e: "click"): void;
}>();
</script>

<template>
  <span
    @click="emit('click')"
    class="tw:rounded-full tw:inline-flex tw:items-center tw:justify-between"
    :class="dynamicClasses"
  >
    <slot />
  </span>
</template>
